import { useCallback, useState } from 'react';
import { readSession, writeSession } from './sessionState.js';

// useState that remembers its value in sessionStorage under `key` (see
// sessionState.js). A changed `key` — another project's detail rendered by the
// same component — loads that key's value instead of carrying the old one
// over. A null key is plain, unpersisted state. `fallback` should be a stable
// value (a module constant), and `sanitize(value, fallback)` guards what is
// read back.
export function useSessionState(key, fallback, sanitize) {
  const [state, setState] = useState(() => ({
    key,
    value: key == null ? fallback : readSession(key, fallback, sanitize),
  }));
  let current = state;
  if (state.key !== key) {
    current = {
      key,
      value: key == null ? fallback : readSession(key, fallback, sanitize),
    };
    setState(current);
  }
  const setValue = useCallback((next) => {
    setState((prev) => {
      const value = typeof next === 'function' ? next(prev.value) : next;
      if (prev.key != null) writeSession(prev.key, value);
      return { key: prev.key, value };
    });
  }, []);
  return [current.value, setValue];
}
